/**
 * "Order compounds" on a board row: pick catalog hits, post them to Simmate.
 *
 * The list is every purchasable hit across every structure, once each, with the
 * best similarity any structure gave it. This is display only — the server rebuilds
 * the same list from the stored board row and refuses anything not on it, so what
 * the box shows and what can be ordered cannot drift apart silently. Corteva hits
 * are in-house stock and never listed; hits measured before SMILES were stored are
 * listed but disabled, because the structure is what Simmate registers.
 *
 * Structures are drawn in the browser by smiles-drawer. No depiction service: a
 * Corteva SMILES must not leave the building to become a picture.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import SmilesDrawer from 'smiles-drawer'
import { listOrders, requestOrder, type TokenGetter } from '../api/leaderboard'
import type { BoardEntry, CatalogHit, CompoundOrder } from '../api/types'
import { formatNumber, formatTimestamp } from '../lib/utils'
import { ConfirmDialog } from './ConfirmDialog'
import { CompoundId } from './CompoundId'
import { Button } from './ui/Button'

/** Mirrors `orders.ORDERABLE_SOURCES` — the server's copy is the one enforced. */
const ORDERABLE = new Set(['enamine', 'chemspace'])

interface Row {
  key: string
  hit: CatalogHit
  pdbId: string
}

function hitId(h: CatalogHit): string {
  return h.catalog_id ?? h.compound_id
}

function orderableRows(entry: BoardEntry): Row[] {
  const best = new Map<string, Row>()
  for (const s of entry.latest_ligand_starts ?? []) {
    for (const h of s.catalog_hits ?? []) {
      const source = h.source?.toLowerCase() ?? ''
      if (!ORDERABLE.has(source)) continue
      const key = `${source}|${hitId(h)}`
      const current = best.get(key)
      if (!current || (h.similarity ?? 0) > (current.hit.similarity ?? 0)) {
        best.set(key, { key, hit: h, pdbId: s.pdb_id })
      }
    }
  }
  return [...best.values()].sort((a, b) => (b.hit.similarity ?? 0) - (a.hit.similarity ?? 0))
}

function Structure({ smiles }: { smiles: string }) {
  const ref = useRef<SVGSVGElement>(null)
  const [failed, setFailed] = useState(false)
  useEffect(() => {
    if (!ref.current) return
    const drawer = new SmilesDrawer.SmiDrawer({ width: 140, height: 100, padding: 4 })
    drawer.draw(smiles, ref.current, 'dark', null, () => setFailed(true))
  }, [smiles])
  if (failed) return <span className="text-2xs text-slate-500">no depiction</span>
  return <svg ref={ref} role="img" aria-label={`Structure of ${smiles}`} className="h-[100px] w-[140px]" />
}

const IN_FLIGHT = new Set(['queued', 'submitting'])

export function OrderCompounds({
  entry,
  getToken,
}: {
  entry: BoardEntry
  getToken: TokenGetter
}) {
  const rows = useMemo(() => orderableRows(entry), [entry])
  const [open, setOpen] = useState(false)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [orders, setOrders] = useState<CompoundOrder[]>([])

  const refresh = useCallback(async () => {
    try {
      setOrders((await listOrders(entry.target_key, getToken)).orders)
    } catch {
      /* the list is informational; the submit path reports its own errors */
    }
  }, [entry.target_key, getToken])

  useEffect(() => {
    void refresh()
  }, [refresh])

  // Poll only while an order is in flight: the runtime takes seconds, not minutes.
  const pending = orders.some((o) => IN_FLIGHT.has(o.status))
  useEffect(() => {
    if (!pending) return
    const id = window.setInterval(() => void refresh(), 3000)
    return () => window.clearInterval(id)
  }, [pending, refresh])

  if (rows.length === 0) return null

  const ready = rows.filter((r) => r.hit.smiles)
  const toggle = (key: string) =>
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })

  const submit = async () => {
    setBusy(true)
    setError(null)
    try {
      const chosen = rows.filter((r) => selected.has(r.key))
      const out = await requestOrder(
        entry.target_key,
        chosen.map((r) => ({ source: r.hit.source ?? '', catalog_id: hitId(r.hit) })),
        note,
        getToken,
      )
      if (!out.created) {
        // The server returned the order already in flight, and discarded this one.
        setError(
          `An earlier order for this target is still ${out.order.status}; this selection was not submitted. Try again once it finishes.`,
        )
        await refresh()
        return
      }
      setOpen(false)
      setSelected(new Set())
      setNote('')
      await refresh()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="mt-3 space-y-1.5">
      <Button size="sm" onClick={() => setOpen(true)}>
        Order compounds
      </Button>
      {orders.slice(0, 3).map((o) => (
        <p key={o.id} className="text-2xs text-slate-400">
          {o.status === 'submitted' ? (
            <>
              Ordered {o.compound_count} on {formatTimestamp(o.requested_at)} as{' '}
              <span className="font-mono text-slate-200">{o.simmate_order_title}</span>
              {o.emailed_at ? ` — confirmation sent to ${o.recipient}` : ' — confirmation e-mail not sent'}
            </>
          ) : o.status === 'failed' ? (
            <span className="text-red-400">Order failed: {o.error}</span>
          ) : (
            <>Posting {o.compound_count} compound(s) to Simmate…</>
          )}
        </p>
      ))}
      {open && (
        <ConfirmDialog
          title="Select compounds to order"
          confirmLabel={`Submit order to Simmate (${selected.size})`}
          confirmDisabled={selected.size === 0}
          busy={busy}
          error={error}
          wide
          onConfirm={() => void submit()}
          onCancel={() => setOpen(false)}
        >
          <p className="text-2xs text-slate-400">
            Unique Enamine and ChemSpace hits across every structure, best similarity
            kept. The order is posted to Simmate as “Submitted by AI” (ASTRA) and the
            confirmation goes to your signed-in address.
          </p>
          <label className="flex items-center gap-2 text-2xs text-slate-300">
            <input
              type="checkbox"
              checked={ready.length > 0 && ready.every((r) => selected.has(r.key))}
              onChange={(e) =>
                setSelected(new Set(e.target.checked ? ready.map((r) => r.key) : []))
              }
            />
            Select all ({ready.length})
          </label>
          <ul className="max-h-[55vh] space-y-2 overflow-y-auto pr-1">
            {rows.map((r) => (
              <li key={r.key} className="flex items-center gap-3 rounded border border-slate-800 p-2">
                <input
                  type="checkbox"
                  aria-label={`Order ${hitId(r.hit)}`}
                  disabled={!r.hit.smiles}
                  checked={selected.has(r.key)}
                  onChange={() => toggle(r.key)}
                />
                {r.hit.smiles ? (
                  <Structure smiles={r.hit.smiles} />
                ) : (
                  <span className="w-[140px] text-2xs text-slate-500">
                    measured before SMILES capture
                  </span>
                )}
                <div className="min-w-0 flex-1 text-2xs">
                  <div>
                    <span className="text-slate-400">{r.hit.source}</span> <CompoundId hit={r.hit} />
                  </div>
                  <div className="text-slate-400">
                    {formatNumber(r.hit.similarity, 2)}{' '}
                    <span className="text-blue-400/80">{r.hit.tier}</span> · from {r.pdbId}
                  </div>
                </div>
              </li>
            ))}
          </ul>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={500}
            rows={2}
            placeholder="Note for the chemist (optional) — added to each compound's comments"
            className="w-full rounded border border-slate-700 bg-slate-950 p-2 text-2xs text-slate-200"
          />
        </ConfirmDialog>
      )}
    </div>
  )
}
